import { StyleSheet } from 'react-native'

import { KycLimitIcon } from '../icons/KycLimitIcon'
import { type KycStatusGlyph, KycStatusIcon } from '../icons/KycStatusIcon'
import { Box, Text, useTheme } from '../theme/restyle'

export interface FiatKycNoticeProps {
  title: string
  detail: string
  /**
   * Set once identity has resolved. The banner then reports an outcome —
   * glyph, title and detail on their own lines, and no tail, since it no
   * longer points at a held deposit.
   */
  status?: KycStatusGlyph
}

/**
 * Identity notice from KYC R2, 2087:8432; the resolved states are KYC
 * Processing Completion, 2387:11504 and siblings.
 */
export function FiatKycNotice({ title, detail, status }: FiatKycNoticeProps) {
  const theme = useTheme()
  const accent = theme.content.elements.primary

  if (status !== undefined) {
    return (
      <Box
        flexDirection="row"
        style={[
          styles.card,
          styles.resolved,
          // The banner covers the header rather than sitting in the layout, so
          // it has to be opaque. A host may make its secondary surface a wash
          // (Fomo's is 8% white), so that tint is laid over the screen's own
          // background instead of over whatever it lands on.
          { backgroundColor: theme.background.modal },
        ]}
        accessibilityRole="alert"
      >
        <Box
          pointerEvents="none"
          style={[
            StyleSheet.absoluteFill,
            { backgroundColor: theme.background.secondary },
          ]}
        />
        <Box style={styles.glyph}>
          <KycStatusIcon glyph={status} color={accent} />
        </Box>
        <Box flex={1} style={styles.copy}>
          <Text weight="medium" style={[styles.text, { color: accent }]}>
            {title}
          </Text>
          <Text
            weight="medium"
            style={[styles.text, styles.detail, { color: accent }]}
          >
            {detail}
          </Text>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      flexDirection="row"
      style={[styles.card, { backgroundColor: theme.background.secondary }]}
      accessibilityRole="alert"
    >
      <Box style={styles.glyph}>
        <KycLimitIcon color={accent} />
      </Box>
      <Box flex={1} style={styles.copy}>
        <Text
          weight="medium"
          style={[styles.text, styles.held, { color: accent }]}
        >
          {title}
          {title && detail ? ' ' : ''}
          {detail}
        </Text>
      </Box>
      <Box
        style={[styles.tip, { borderTopColor: theme.background.secondary }]}
      />
    </Box>
  )
}

const styles = StyleSheet.create({
  card: {
    paddingVertical: 14,
    paddingLeft: 22,
    paddingRight: 10,
    gap: 12,
    borderRadius: 16,
    minHeight: 64,
  },
  // 364x87 at radius 20, with the copy starting 73 in from the card's left.
  resolved: {
    overflow: 'hidden',
    paddingVertical: 15,
    gap: 18,
    borderRadius: 20,
    minHeight: 87,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  glyph: { justifyContent: 'center' },
  copy: { justifyContent: 'center' },
  text: { fontSize: 15, lineHeight: 18, letterSpacing: -0.225 },
  held: { fontSize: 14.5, letterSpacing: -0.145 },
  // Figma caps the detail at 245 so it breaks a line earlier than the
  // card's own width would.
  detail: { opacity: 0.5, maxWidth: 245 },
  tip: {
    position: 'absolute',
    bottom: -10,
    left: 25,
    borderLeftWidth: 11.5,
    borderRightWidth: 11.5,
    borderTopWidth: 10,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
})
